import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { SUPPORTS, STATUSES } from "./Projects";
import { Wrench, FolderKanban, CheckCircle2, Users, Clock, ChevronRight, ListChecks, AlertCircle } from "lucide-react";

const ACTIVE_OUT = ["termine", "livree", "facturee", "annule"];
const statusLabel = (k) => (STATUSES.find((s) => s.key === k)?.label) || k;
const supportOf = (t) => SUPPORTS[t] || SUPPORTS.autre;

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`atelier-stat-${label}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tint}`}><Icon className="w-5 h-5" /></div>
      <div><div className="text-2xl font-display font-bold text-navy tabular leading-none">{value}</div><div className="text-xs text-slate-500 mt-1">{label}</div></div>
    </div>
  );
}

export default function Atelier() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {}); }, []);

  const active = projects.filter((p) => !ACTIVE_OUT.includes(p.status));
  const tasks = [];
  active.forEach((p) => (p.stages || []).forEach((s) => { if (!s.done) tasks.push({ p, label: s.label, responsible: (s.responsible || "").trim() || "Non assigné", due: s.date }); }));
  const doneCount = projects.reduce((n, p) => n + (p.stages || []).filter((s) => s.done).length, 0);

  const byResp = {};
  tasks.forEach((t) => { byResp[t.responsible] = (byResp[t.responsible] || 0) + 1; });
  const respList = Object.entries(byResp).sort((a, b) => b[1] - a[1]);
  const maxLoad = Math.max(1, ...Object.values(byResp));
  const palette = ["#C28F63", "#2563EB", "#34A853", "#E8823A", "#8B5CF6"];

  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <h1 className="text-3xl lg:text-4xl font-display font-bold text-navy tracking-tight">Atelier</h1>
        <p className="text-slate-500 mt-1">La charge de travail et les tâches en cours, reliées à vos projets réels.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={FolderKanban} label="Projets actifs" value={active.length} tint="bg-cognac-50 text-cognac-600" />
        <Stat icon={ListChecks} label="Tâches à faire" value={tasks.length} tint="bg-sky-50 text-sky-600" />
        <Stat icon={CheckCircle2} label="Étapes terminées" value={doneCount} tint="bg-emerald-50 text-emerald-600" />
        <Stat icon={Users} label="Personnes actives" value={respList.filter(([n]) => n !== "Non assigné").length} tint="bg-indigo-50 text-indigo-600" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Charge par personne */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 min-w-0" data-testid="atelier-charge">
          <h3 className="font-display font-semibold text-navy text-lg mb-4">Charge par personne</h3>
          <div className="space-y-4">
            {respList.map(([name, load], i) => (
              <div key={name} className="flex items-center gap-3" data-testid={`atelier-resp-${name}`}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: palette[i % palette.length] }}>{name.slice(0, 1).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between"><span className="text-sm font-semibold text-navy truncate">{name}</span><span className="text-xs font-bold text-navy tabular">{load} tâche{load > 1 ? "s" : ""}</span></div>
                  <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${(load / maxLoad) * 100}%`, background: palette[i % palette.length] }} /></div>
                </div>
              </div>
            ))}
            {respList.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Aucune tâche en cours. Ajoutez des étapes à vos projets.</p>}
          </div>
        </div>

        {/* Tâches en cours */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5 min-w-0" data-testid="atelier-tasks">
          <h3 className="font-display font-semibold text-navy text-lg mb-3">Tâches en cours</h3>
          <div className="space-y-2 max-h-[52vh] overflow-y-auto scrollbar-thin">
            {tasks.map((t, i) => {
              const sup = supportOf(t.p.support_type);
              const overdue = t.due && new Date(t.due) < new Date();
              return (
                <button key={i} onClick={() => navigate(`/projets/${t.p.id}`)} data-testid={`atelier-task-${i}`}
                  className="w-full text-left flex items-center gap-3 py-2 px-2.5 rounded-xl border border-slate-100 hover:bg-slate-50">
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${overdue ? "bg-rose-500" : "bg-amber-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-navy truncate">{t.label} <span className="text-slate-400 font-normal">— {t.p.title}</span></div>
                    <div className="text-[11px] text-slate-400 truncate">{sup.label} · {t.responsible}</div>
                  </div>
                  {t.due && <span className={`text-[11px] font-mono shrink-0 ${overdue ? "text-rose-500 font-semibold" : "text-slate-400"}`}>{t.due}</span>}
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>
              );
            })}
            {tasks.length === 0 && <p className="text-sm text-slate-400 py-10 text-center"><Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />Aucune tâche en attente.</p>}
          </div>
        </div>
      </div>

      {/* Projets actifs */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="atelier-projects">
        <h3 className="font-display font-semibold text-navy text-lg mb-3">Projets actifs</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {active.map((p) => {
            const st = p.stages || []; const done = st.filter((s) => s.done).length; const pct = st.length ? Math.round((done / st.length) * 100) : 0;
            const sup = supportOf(p.support_type);
            return (
              <button key={p.id} onClick={() => navigate(`/projets/${p.id}`)} data-testid={`atelier-project-${p.id}`}
                className="text-left rounded-xl border border-slate-100 hover:border-cognac-300 hover:shadow-sm p-4 transition-all min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-navy truncate">{p.title}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0">{statusLabel(p.status)}</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5 truncate">{sup.label}{p.client_name ? ` · ${p.client_name}` : ""}</div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-gradient-to-r from-cognac-400 to-cognac-500" style={{ width: `${pct}%` }} /></div>
                  <span className="text-[11px] font-mono text-slate-500 shrink-0">{pct}%</span>
                </div>
              </button>
            );
          })}
          {active.length === 0 && <p className="text-sm text-slate-400 py-6 text-center col-span-full">Aucun projet actif.</p>}
        </div>
      </div>
    </div>
  );
}
